import React, { useState, useCallback, useEffect } from 'react';
import { pbAuthedFetch } from "../../authHeaders";
import { Meter, Legend, SummaryBand } from './viz';
import { TitleIcon } from './icons';

// Read-only view of the append-only audit trail.
// Entries: { id, action, resource, resource_id, detail, at, actor_id, actor_email, actor_name }

const CATEGORY = [
  { tone: 'pill--risk', kind: 'Destructive', match: ['purge', 'purge-expired', 'delete'] },
  { tone: 'pill--ok', kind: 'Recovery', match: ['restore'] },
  { tone: 'pill', kind: 'Access', match: ['role', 'user-status', 'user-pages', 'user-group', 'group-create', 'group-update', 'group-delete'] },
  { tone: 'pill', kind: 'Account', match: ['user-create', 'user-resend-invite'] },
  { tone: 'pill', kind: 'Configuration', match: ['connector-config'] },
];

function categorize(action: string) {
  for (const c of CATEGORY) if (c.match.indexOf(action) !== -1) return c;
  return { tone: 'pill', kind: 'Other' };
}

const TONE_FILL: Record<string, string> = {
  'pill--risk': 'risk',
  'pill--ok': 'ok',
  'pill': 'steel',
};

function actionMix(entries: AuditEntry[]) {
  const kinds = [...CATEGORY, { tone: 'pill', kind: 'Other' }];
  const counts = new Map(kinds.map((c) => [c.kind, 0]));
  for (const e of entries) {
    const k = categorize(e.action).kind;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  return kinds.map((c) => ({
    key: c.kind,
    name: c.kind,
    tone: TONE_FILL[c.tone] || 'steel',
    value: counts.get(c.kind) || 0,
  }));
}

function stamp(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return String(at || '');
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 19)}Z`;
}

function actorOf(e: AuditEntry): string {
  if (e.actor_name) return e.actor_name;
  if (e.actor_email) return e.actor_email;
  return e.actor_id ? `user ${e.actor_id}` : 'account removed';
}

function detailOf(e: AuditEntry): string {
  if (e.detail == null) return '';
  if (typeof e.detail !== 'object') return String(e.detail);
  const parts = Object.keys(e.detail).map((k) => `${k}=${JSON.stringify((e.detail as Record<string, unknown>)[k])}`);
  return parts.join(' · ');
}

const LIMITS = [50, 100, 250];

interface AuditEntry {
  id: number;
  action: string;
  resource: string;
  resource_id: string | null;
  detail: string | Record<string, unknown> | null;
  at: string;
  actor_id: number | null;
  actor_email: string | null;
  actor_name: string | null;
}

interface IngestEvent {
  id: number;
  event_type: string;
  actor_type: string | null;
  detail: string | Record<string, unknown> | null;
  occurred_at: string;
  model_id: string | null;
  prompt_version: string | null;
}

function istamp(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return String(at || '');
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 19)}Z`;
}

function idetail(e: IngestEvent): string {
  if (e.detail == null) return '';
  if (typeof e.detail !== 'object') return String(e.detail);
  return Object.keys(e.detail).map((k) => `${k}=${JSON.stringify((e.detail as Record<string, unknown>)[k])}`).join(' · ');
}

function IngestAuditPanel() {
  const [events, setEvents] = useState<IngestEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pbAuthedFetch('/api/ingest/audit?limit=100')
      .then((res) => res.json().catch(() => ({})).then((d) => ({ res, d })))
      .then(({ res, d }: { res: Response; d: any }) => {
        if (!res.ok) throw new Error(d.error || `request failed (${res.status})`);
        if (!cancelled) setEvents(Array.isArray(d.events) ? d.events : []);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .then(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="box" style={{ marginTop: 16 }}>
      <header className="box__head">
        <TitleIcon name="book" />
        <div className="box__head-text">
          <p className="box__eyebrow">Classify · extract · plan · approve · apply</p>
          <h2 className="box__title">Document ingest</h2>
        </div>
        <div className="box__head-actions">
          <span className="stg-boxhead-note">{loading ? 'Loading…' : `${events.length} most recent ingest ${events.length === 1 ? 'event' : 'events'}`}</span>
        </div>
      </header>
      <div className={`box__body${events.length && !loading && !error ? ' box__body--flush' : ''}`}>
        {loading ? <div className="state"><div className="state__title">Loading ingest audit trail…</div></div> : null}
        {!loading && error ? <div className="state state--risk"><div className="state__title">Could not load ingest audit</div><div className="state__body">{error}</div></div> : null}
        {!loading && !error && events.length === 0 ? <div className="state"><div className="state__title">No ingest activity yet</div></div> : null}
        {!loading && !error && events.length > 0 ? (
          <div className="tbl-scroll">
            <table className="tbl">
              <caption className="pb-sr-only">Document ingest audit — time in UTC, the event, and any recorded detail.</caption>
              <thead>
                <tr>
                  <th scope="col">When (UTC)</th>
                  <th scope="col">Event</th>
                  <th scope="col">Detail</th>
                  <th scope="col">Model</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id}>
                    <td className="stg-fig">{istamp(e.occurred_at)}</td>
                    <td><span className="pill">{e.event_type}</span></td>
                    <td className="stg-fig stg-detail">{idetail(e)}</td>
                    <td className="stg-fig">{e.model_id || '—'}{e.prompt_version ? ` · ${e.prompt_version}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback((n: number) => {
    setLoading(true);
    setError('');
    pbAuthedFetch(`/api/admin/audit?limit=${n}`)
      .then((res) => res.json().catch(() => ({})).then((d) => ({ res, d })))
      .then(({ res, d }: { res: Response; d: any }) => {
        if (!res.ok) throw new Error(d.error || `request failed (${res.status})`);
        setEntries(Array.isArray(d.entries) ? d.entries : []);
      })
      .catch((e) => setError(e.message))
      .then(() => setLoading(false));
  }, []);

  useEffect(() => { load(limit); }, [load, limit]);

  const mix = actionMix(entries);
  const destructive = mix.find((m) => m.key === 'Destructive');

  return (
    <section aria-label="Audit trail">
      <div className="box">
        <header className="box__head">
          <TitleIcon name="history" />
          <div className="box__head-text">
            <p className="box__eyebrow">Append-only · read-only · times are UTC</p>
            <h2 className="box__title">Audit trail</h2>
          </div>
          <div className="box__head-actions">
            <span className="stg-boxhead-note">{loading ? 'Loading…' : `${entries.length} most recent ${entries.length === 1 ? 'entry' : 'entries'}`}</span>
            <div className="stg-filter">
              <label className="stg-label" htmlFor="stg-audit-limit">Show</label>
              <select className="stg-input pb-sel-sm" id="stg-audit-limit" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                {LIMITS.map((n) => <option key={n} value={n}>{n} entries</option>)}
              </select>
            </div>
          </div>
        </header>
        {!loading && !error && entries.length > 0 ? (
          <SummaryBand inset eyebrow="Action mix" value={`${destructive ? destructive.value : 0} destructive of ${entries.length}`}>
            <Meter label="Action mix" total={entries.length} segments={mix} />
            <Legend items={mix} />
          </SummaryBand>
        ) : null}
        <div className={`box__body${entries.length && !loading && !error ? ' box__body--flush' : ''}`}>
          {loading ? <div className="state"><div className="state__title">Loading audit trail…</div></div> : null}
          {!loading && error ? (
            <div className="state state--risk">
              <div className="state__title">Could not load the audit trail</div>
              <div className="state__body">{error}<br /><button className="btn btn--sm" onClick={() => load(limit)} style={{ marginTop: 8 }}>Retry</button></div>
            </div>
          ) : null}
          {!loading && !error && entries.length === 0 ? (
            <div className="state"><div className="state__title">Nothing recorded yet</div><div className="state__body">Connector saves, user and group changes, restores and purges are recorded here.</div></div>
          ) : null}
          {!loading && !error && entries.length > 0 ? (
            <div className="tbl-scroll">
              <table className="tbl">
                <caption className="pb-sr-only">Audit trail — time in UTC, who acted, what action, which resource, and any recorded detail.</caption>
                <thead>
                  <tr>
                    <th scope="col">When (UTC)</th>
                    <th scope="col">Who</th>
                    <th scope="col">Action</th>
                    <th scope="col">Resource</th>
                    <th scope="col">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const cat = categorize(e.action);
                    return (
                      <tr key={e.id}>
                        <td className="stg-fig">{stamp(e.at)}</td>
                        <td className="stg-strong">{actorOf(e)}</td>
                        <td><span className={`pill ${cat.tone}`} title={`${cat.kind} action`}>{e.action}</span></td>
                        <td className="stg-fig">{e.resource}{e.resource_id ? ` · ${e.resource_id}` : ''}</td>
                        <td className="stg-fig stg-detail">{detailOf(e)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
      <IngestAuditPanel />
    </section>
  );
}
